<template>
  <QsLazyload :rows="5" render-on-idle>
    <vxe-table
      ref="vxeTableRef"
      :key="tableKey"
      border
      :row-config="{ isHover: true, isCurrent: true }"
      :column-config="{ resizable: true }"
      :scroll-x="{ scrollToLeftOnChange: true }"
      :scroll-y="{ scrollToTopOnChange: true }"
      :sort-config="getSortconfig()"
      v-bind="$attrs"
      :data="data"
      @resizable-change="saveColumnWidths"
      @sort-change="sortChange"
      @header-cell-click="headerCellClick"
      @cell-click="cellClick"
    >
      <vxe-column
        v-if="checkbox"
        key="checkbox"
        type="checkbox"
        fixed="left"
        align="center"
        width="40"
      />
      <vxe-column
        v-if="expand"
        key="expand"
        type="expand"
        fixed="left"
        width="60"
      >
        <template #content="{ row, rowIndex }">
          <slot name="expand-slot" v-bind="{ row, rowIndex }"></slot>
        </template>
      </vxe-column>
      <vxe-column
        v-if="radio"
        key="radio"
        type="radio"
        fixed="left"
        align="center"
        width="40"
      />
      <vxe-column
        v-if="seq"
        key="seq"
        title="序号"
        type="seq"
        fixed="left"
        align="center"
        width="60"
      ></vxe-column>
      <vxe-column
        v-for="column in columns"
        :key="column.field"
        :title="column.title"
        :align="column.align || 'left'"
        :field="column.field"
        :fixed="column.fixed ?? ''"
        :header-align="column.headerAlign || column.align || 'left'"
        :formatter="column.formatter"
        :params="{ params: column.params, default: column.default }"
        :cell-render="{ name: column.render }"
        :sortable="sortable && (column?.sortable ?? false)"
        :width="columnWidths[column.field] || getColumnWidth(column)"
        :min-width="column.width || 120"
        :title-prefix="column?.tips && { content: column.tips }"
        :title-help="column?.titleHelp && { message: column?.titleHelp }"
        :type="column.renderType"
        :show-overflow="column.showOverflow"
        :class-name="column.className"
        :header-class-name="
          column?.headerClassName ??
          (column?.batch ? 'color-theme cursor-pointer' : '')
        "
        :tree-node="column.treeNode"
        :drag-sort="column.dragSort"
      >
        <template v-if="$slots.header" #header="{ ...slotProps }">
          <slot name="header" v-bind="{ ...slotProps }"></slot>
        </template>
        <template v-if="showColumn(column)" #default="{ row, rowIndex }">
          <template v-if="column.formatter && column.showTooltip">
            <div class="ws-pre">
              <!-- 截断使用formatter渲染的文本 -->
              <EllipsisTooltip
                :class="column.class ?? ''"
                :line="column.line"
                :content="
                  column.formatter({
                    cellValue: row[column.field],
                    row,
                    column,
                  })
                "
              />
            </div>
          </template>
          <template v-else-if="column.type === 'slot'">
            <slot
              v-if="column.slotName"
              :name="column.slotName"
              v-bind="{ column, row, rowIndex }"
            ></slot>
            <slot
              v-else
              name="column-slot"
              v-bind="{ column, row, rowIndex }"
            ></slot>
          </template>
          <template v-else-if="column.type === 'operate'">
            <slot name="operate" v-bind="{ column, row, rowIndex }"></slot>
          </template>
          <template v-else-if="column.type === 'image'">
            <template v-if="checkTypeByRow(column, row)">
              <template v-if="!column.showImgPopover">
                <a-image
                  v-if="row[column.field]"
                  :width="column.imgWidth ?? 30"
                  :height="column.imgWidth ?? 30"
                  class="pointer main-img"
                  :src="
                    useDealWithPic
                      ? replacePicAlias(row[column.field], 'pclist')
                      : row[column.field]
                  "
                  :preview-props="({ src: row[column.field] } as ImagePreviewProps)"
                  @click.stop
                />
                <img
                  v-else
                  class="main-img"
                  :style="{
                    width: (column.imgWidth ?? 30) + 'px',
                    height: (column.imgWidth ?? 30) + 'px',
                  }"
                  src="@/assets/images/no-content-img.png"
                />
              </template>
              <ListImgPopover
                v-else
                :img-url="row[column.field]"
                :width="column.imgWidth ?? 30"
                :height="column.imgWidth ?? 30"
                :use-deal-with-pic="useDealWithPic"
              />
            </template>
            <template v-else>{{ column?.default }}</template>
          </template>
          <template v-else-if="column.type === 'images'">
            <QsUploader
              v-if="row[column.field]?.length || column.showUpload"
              :file-list="row[column.field]"
              accept="image/*"
              :show-upload-remove-btn="!!column.showUpload"
              mini-size
            />
            <img
              v-else-if="column.defaultImg"
              class="no-content-img"
              :style="{
                width: (column.imgWidth ?? 40) + 'px',
                height: (column.imgWidth ?? 40) + 'px',
              }"
              src="@/assets/images/no-content-img.png"
            />
            <template v-else>-</template>
          </template>
          <template v-else-if="column.type === 'route'">
            <div
              v-if="row[column.field] !== undefined"
              class="color-theme pointer ws-pre"
              @click="routeTo(column, row, rowIndex)"
            >
              <!-- 截断商品名称等跳转文本 -->
              <EllipsisTooltip
                v-if="column.showTooltip"
                :class="column.class ?? ''"
                :content="row[column.field]"
              />
              <span v-else>{{ row[column.field] ?? column?.default }}</span>
            </div>
            <span v-else>{{ column?.default }}</span>
          </template>
          <template v-else-if="column.type === 'day'">
            {{
              formatTime(
                row?.[column.field],
                column?.format ?? 'YYYY-MM-DD',
                '-'
              )
            }}
          </template>
          <template v-else-if="column.type === 'modal'">
            <div class="modal-slot">
              <slot name="modal" v-bind="{ column, row, rowIndex }"></slot>
            </div>
          </template>
          <template v-else-if="column.type === 'inputPrice'">
            <a-input-number
              :ref="(el) => bindInputFocusRef(el, row, column.field)"
              v-model.number="row[column.field]"
              :placeholder="column?.placeholder ?? '请输入'"
              :min="column?.min ?? 0"
              :max="column?.max ?? globalContants.MAXNINEDIGITNUM"
              :precision="column?.precision ?? 2"
              :class="column.inputClass"
              @focus="focusInput($event, { column, row, rowIndex })"
              @blur="blurInput($event, { column, row, rowIndex })"
              @keydown="handleKeyDownInput($event, rowIndex, column.field)"
              @change="changeInput($event, { column, row, rowIndex })"
            >
              <template #prefix>￥</template>
            </a-input-number>
          </template>
          <template
            v-else-if="
              column.type === 'inputNumber' && checkTypeByRow(column, row)
            "
          >
            <div class="flex-center">
              <a-input-number
                :ref="(el) => bindInputFocusRef(el, row, column.field)"
                v-model.number="row[column.field]"
                :placeholder="column?.placeholder ?? '请输入'"
                :min="column?.min ?? -globalContants.MAXNINEDIGITNUM"
                :max="
                  (isFunction(column.getMax)
                    ? column.getMax({ row, column })
                    : column?.max) ?? globalContants.MAXNINEDIGITNUM
                "
                :precision="column?.precision"
                :disabled="
                  isFunction(column.disabled)
                    ? column.disabled({ column, row, rowIndex })
                    : false
                "
                :class="column.inputClass"
                :allow-clear="column.allowClear"
                :hide-button="column.hideButton"
                :error="column?.inputError?.({ column, row, rowIndex })"
                @keydown="handleKeyDownInput($event, rowIndex, column.field)"
                @focus="focusInput($event, { column, row, rowIndex })"
                @blur="blurInput($event, { column, row, rowIndex })"
                @change="changeInput($event, { column, row, rowIndex })"
                @click.stop
              />
              <slot
                v-if="column.slotName"
                :name="column.slotName"
                v-bind="{ column, row, rowIndex }"
              ></slot>
            </div>
          </template>
          <template v-else-if="column.type === 'inputTextarea'">
            <a-textarea
              :ref="(el) => bindInputFocusRef(el, row, column.field)"
              v-model="row[column.field]"
              :max-length="column?.maxLength ?? 255"
              :placeholder="column?.placeholder ?? '请输入'"
              :auto-size="{
                minRows: 1,
                maxRows: 5,
              }"
              allow-clear
              @keydown="handleKeyDownInput($event, rowIndex, column.field)"
              @focus="focusInput($event, { column, row, rowIndex })"
              @blur="blurInput($event, { column, row, rowIndex })"
              @change="changeInput($event, { column, row, rowIndex })"
            />
          </template>
          <template
            v-else-if="
              column.type === 'inputText' && checkTypeByRow(column, row)
            "
          >
            <a-input
              :ref="(el) => bindInputFocusRef(el, row, column.field)"
              v-model.trim="row[column.field]"
              :placeholder="column?.placeholder ?? '请输入'"
              :max-length="column?.maxLength ?? 255"
              allow-clear
              :disabled="
                isFunction(column.disabled)
                  ? column.disabled({ column, row, rowIndex })
                  : false
              "
              @keydown="handleKeyDownInput($event, rowIndex, column.field)"
              @focus="focusInput($event, { column, row, rowIndex })"
              @blur="blurInput($event, { column, row, rowIndex })"
              @change="changeInput($event, { column, row, rowIndex })"
            />
          </template>
          <template v-else-if="row[column.field] === undefined">
            {{ column?.default }}
          </template>
          <template v-else-if="column.showTooltip">
            <div class="ws-pre">
              <!-- 截断备注等比较长的 -->
              <EllipsisTooltip
                :class="column.class ?? ''"
                :content="formatterField({ column, row })"
              />
            </div>
          </template>
          <template v-else>
            {{ formatterField({ column, row }) }}
          </template>
        </template>
      </vxe-column>
    </vxe-table>
    <Pager v-if="pageVisible" v-bind="$attrs" />
  </QsLazyload>
  <a-modal
    v-model:visible="batchData.show"
    title="批量输入"
    @ok="handleBatchModalOk"
    @cancel="handleBatchModalCancel"
  >
    <div class="flex-center">
      <div class="mr12">{{ batchData.column?.title }}：</div>
      <a-input-number
        ref="batchNumberRef"
        v-model="batchData.val"
        :min="batchData.column?.min ?? 0"
        :max="batchData.column?.max ?? globalContants.MAXNINEDIGITNUM"
        :precision="batchPrecision"
        :style="{ width: '320px' }"
        placeholder="请输入"
      />
    </div>
  </a-modal>
</template>

<script lang="ts" setup>
  import { ref, reactive, nextTick, watchEffect, computed, watch } from 'vue';

  import type { VxeTablePropTypes } from 'vxe-table';

  import { cloneDeep, isFunction } from 'lodash';

  import useTableGetNextLineFocus from '@/hooks/useTableGetNextLineFocus';

  import {
    getColumnWidth,
    showColumn,
    formatterField,
  } from '@/components/BasisTable/common';
  import QsLazyload from '@/components/QsLazyload/index.vue';
  import useColumnWidth from '@/hooks/useColumnWidth';

  import globalContants from '@/constants/globalContants';
  import { formatTime, replacePicAlias } from '@/utils/common';

  import type { ImagePreviewProps } from '@arco-design/web-vue/es/image/interface';
  import Pager from './pager.vue';

  interface ITableProps {
    checkbox?: boolean;
    expand?: boolean;
    radio?: boolean;
    seq?: boolean;
    pageVisible?: boolean;
    columns: Array<any>;
    data: VxeTablePropTypes.Data;
    sortable?: boolean; // 是否支持排序(禁用排序时可以传false)
    batchSort?: boolean;
    defaultSort?: Recordable | Recordable[]; // 用于默认高亮排序
    maxSortLen?: number; // 用于组合排序，最大排序
    replaceFirstSort?: boolean; // 用于组合排序batchSort达到最大排序时，是否替换数组最前面的排序
    usedKeydown?: boolean; // 是否使用快捷键切换输入框
    useDealWithPic?: boolean; // 是否使用图片压缩
    tableColumnWidthKey?: string; // 用于保存表格列宽的key
    useSeparateTableColumnKey?: boolean; // 是否使用单独列宽key
  }
  const props = withDefaults(defineProps<ITableProps>(), {
    pageVisible: false,
    sortable: true,
    batchSort: false,
    usedKeydown: true,
    useDealWithPic: true,
    maxSortLen: 3,
    replaceFirstSort: false,
  });
  const emits = defineEmits<{
    (e: 'routeTo', data: any): void;
    (e: 'changeInput', data: any): void;
    (e: 'focusInput', data: any): void;
    (e: 'blurInput', data: any): void;
    (
      e: 'sortChange',
      data:
        | { sidx: string; sord: string }
        | Array<{ sidx: string; sord: string }>,
      isMaxium?: boolean
    ): void;
    (e: 'batchChangeInput', data: any): void;
  }>();
  const defaultSortArr = computed(() => {
    if (Array.isArray(props?.defaultSort) && props?.defaultSort.length) {
      return props?.defaultSort.map((item: any) => {
        return {
          field: item.sidx ?? '',
          order: item.sord,
        };
      });
    }
    return [];
  });

  const { saveColumnWidths, columnWidths } = useColumnWidth(
    props.tableColumnWidthKey,
    {
      useSeparateKey: props.useSeparateTableColumnKey,
    }
  );

  const vxeTableRef = ref<any>();
  const sortList = ref<any[]>([]);
  const routeTo = (column: any, row: any, rowIndex: number) => {
    emits('routeTo', {
      column,
      row,
      rowIndex,
      cellValue: row?.[column?.field],
    });
  };
  const checkTypeByRow = (column: Recordable, row: Recordable) => {
    const { checkTypeFun } = column;
    if (!checkTypeFun || !isFunction(checkTypeFun)) return true;
    return checkTypeFun({ column, row });
  };
  const updateFooter = () => {
    nextTick(() => {
      vxeTableRef?.value?.updateFooter();
    });
  };
  // 可以在column中指定inputFormatter为输入内容格式化
  const changeInput = (value: any, data: Recordable) => {
    const { row, column } = data;
    const { field, inputFormatter } = column;
    if (inputFormatter) {
      value = inputFormatter(value, data);
      row[field] = value;
    }
    emits('changeInput', { ...data, value });
  };

  const focusInput = (e: any, data: Recordable) => {
    const { column } = data;
    focusInputByNextLineHooks(e, column.field);
    emits('focusInput', { ...data, ev: e });
  };
  const blurInput = (e: any, data: Recordable) => {
    emits('blurInput', { ...data, ev: e });
  };
  // 排序
  const sortChange = (data: any) => {
    if (props?.batchSort) {
      let isMaxium = false;
      // 处理sortList顺序问题
      const sortIndex = sortList.value.findIndex(
        (item: any) => item.field === data.field
      );
      const newObj: any = { field: data.field, order: data.order };
      if (sortIndex !== -1) {
        if (data.order) {
          sortList.value.splice(sortIndex, 1, newObj);
        } else {
          sortList.value.splice(sortIndex, 1);
        }
      } else if (data.order) {
        if (sortList.value.length < props.maxSortLen) {
          sortList.value.push(newObj);
        } else {
          isMaxium = true;
          if (props.replaceFirstSort) {
            sortList.value.shift();
            sortList.value.push(newObj);
          }
        }
      }
      const realSortList = sortList.value.length
        ? sortList.value.map((item: any) => ({
            sidx: item.field,
            sord: item.order,
          }))
        : [];
      emits('sortChange', realSortList, isMaxium);
      return;
    }
    if (data.order) {
      emits('sortChange', { sidx: data.field, sord: data.order });
    } else {
      emits('sortChange', { sidx: '', sord: '' });
    }
  };
  const clearSortList = () => {
    vxeTableRef.value?.clearSort();
    sortList.value = [];
  };
  // 默认高亮排序值段
  const getSortconfig = () => {
    const config: Recordable = { remote: true };
    if (props?.batchSort) {
      config.multiple = true;
      if (Array.isArray(props?.defaultSort) && props?.defaultSort?.length) {
        config.defaultSort = props?.defaultSort.map((item: any) => {
          return {
            field: item.sidx ?? '',
            order: item.sord,
          };
        });
      }
      return config;
    }
    if (!Array.isArray(props?.defaultSort) && props?.defaultSort?.sord) {
      config.defaultSort = {
        field: props.defaultSort?.sidx ?? '',
        order: props.defaultSort.sord,
      };
    }
    return config;
  };
  // 批量输入数量
  const batchNumberRef = ref<any>();
  const batchData: Record<string, any> = reactive({
    column: {}, // 批量输入的列
    val: undefined,
    show: false,
  });
  // 点击表头
  const headerCellClick = ({ column, columnIndex }: any) => {
    if (!props.data?.length) return;

    const getColumn =
      (props.columns.find((col) => col.field === column.field) ||
        props.columns?.[columnIndex]) ??
      {};
    const { batch, title, field } = getColumn;
    if (batch) {
      // 将column中的batch设置为true即可点击表头批量输入数字
      batchData.column = getColumn;
      batchData.show = true;
      batchData.val = undefined;
      nextTick(() => {
        batchNumberRef.value?.focus();
      });
    }
  };
  // 点击单元格
  const cellClick = ({
    row,
    rowIndex,
    column: vxeColumn,
    columnIndex,
  }: {
    row: any;
    rowIndex: number;
    column: any;
    columnIndex: number;
  }) => {
    let column = props.columns?.[columnIndex];
    if (column?.field !== vxeColumn.field) {
      // 当存在radio checkbox seq 等独立列时，columnIndex不能取到正确的column
      column =
        props.columns?.find((col) => col.field === vxeColumn.field) ??
        vxeColumn;
    }
    const show = showColumn(column);
    if (show) return; // 排除已经用template渲染的列
    const { type } = column;
    if (type === 'route') {
      routeTo(column, row, rowIndex);
    }
  };
  const handleBatchModalOk = () => {
    // 格式化数据
    const { column, val } = batchData;
    const { field, inputFormatter, filterDataFun } = column;
    const data = isFunction(filterDataFun)
      ? filterDataFun(props.data)
      : props.data;
    if (inputFormatter) {
      data.forEach((row: Recordable) => {
        row[field] = inputFormatter(val, { row, column });
      });
    } else {
      data.forEach((row: Recordable) => {
        row[field] = val;
      });
    }
    emits('batchChangeInput', field);
    handleBatchModalCancel();
    updateFooter();
  };
  const handleBatchModalCancel = () => {
    batchData.show = false;
    batchData.val = undefined;
    batchData.column = {};
  };
  const tableKey = ref<number>(1);
  // 刷新refreshColumn
  const refreshColumn = (e?: any) => {
    // 改变tableKey是为了更新表格虚拟DOM，解决当自定义列改变时，字段的排序位置没有刷新(导致这个问题的原因是列顺序改变时vxe-column的colid没有刷新)
    tableKey.value += 1;
    // 自定义列后要在父组件调用这个函数刷新表格，否则会造成自定义列后列改变了数据没有改变
    nextTick(() => {
      nextTick(() => {
        vxeTableRef?.value?.refreshColumn();
      });
    });
  };

  const inputRefTypeList = computed(() => {
    return props.columns
      .filter(
        (col: Recordable) =>
          col.type &&
          ['inputPrice', 'inputNumber', 'inputTextarea', 'inputText'].includes(
            col.type
          )
      )
      .map((col: Recordable) => col.field);
  });
  const {
    bindInputFocusRef,
    handleKeyDownInput,
    focusInput: focusInputByNextLineHooks,
  } = useTableGetNextLineFocus({
    tableRef: vxeTableRef,
    readonly: !props.usedKeydown,
    typeList: inputRefTypeList,
  });

  const batchPrecision = computed(() => {
    const { precision, batchPrecision } = batchData.column;
    return precision ?? batchPrecision ?? 0;
  });

  watchEffect(() => {
    // 参数只是为了触发监听，没有实际作用
    refreshColumn(props.columns);
  });
  watch(
    () => defaultSortArr.value,
    (newVal: any) => {
      // 有默认排序规则初始化时赋值sortList
      if (newVal.length && !sortList.value.length) {
        sortList.value = newVal;
      }
    },
    {
      immediate: true,
      deep: true,
    }
  );
  defineExpose({
    vxeTableRef,
    updateFooter,
    refreshColumn,
    clearSortList,
  });
</script>

<script lang="ts">
  export default {
    name: 'BasisTable',
  };
</script>

<style lang="less" scoped>
  .no-content-img {
    height: 100%;
  }
</style>
